import React from 'react';

import DropdownPanel from '@uikit/old/DropdownPanel/DropdownPanel';
import { PublishersOrderType } from '@ts/publishers.types';
import InsertionBrief from '@components/shared/InsertionBrief/InsertionBrief';
import {
  getInsertionBriefUrl,
  isLinkInsertionOrder,
} from '@components/Publisher/Orders/OrdersTable/utils';
import { INSERTION_BRIEF_COPY } from '@components/Publisher/Orders/OrdersTable/constants';

const EMPTY_VALUE = '-';

function ExternalLink({ url }: { url: string }): JSX.Element {
  if (!url) {
    return <span>{EMPTY_VALUE}</span>;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
}

function PlacementDetails({ data }: { data: PublishersOrderType }): JSX.Element {
  // An order can carry several backlinks; the details panel summarises them.
  const backlinks = data.backlinksList || [];
  const targetUrls = backlinks.map(({ url }) => url).filter(Boolean);
  const anchors = backlinks.map(({ anchor }) => anchor).filter(Boolean);

  const isLinkInsertion = isLinkInsertionOrder(data);
  const briefUrl = getInsertionBriefUrl(data);
  // While `documentUrl` is the brief it is an instruction, not a publication
  // URL, so it moves into the brief block instead of this cell.
  const publicationUrl = briefUrl ? '' : data.documentUrl;

  return (
    <DropdownPanel
      initiallyOpen={false}
      classNames={{
        container: 'dropdown-backlinks__dropdown',
        header: 'dropdown-backlinks',
      }}
      headerText={
        <div className="dropdown-backlinks-content">
          <div className="dropdown-backlinks-content__title">Order details</div>
        </div>
      }
    >
      <div className="dropdown-backlinks-content__details">
        <div className="dropdown-backlinks-content__details-top">
          <div className="dropdown-backlinks-content__details-top__item">
            <div className="dropdown-backlinks-content__details-top__item__title">TARGET URL</div>
            <div className="dropdown-backlinks-content__details-top__text">
              {targetUrls.length
                ? targetUrls.map((url, index) => (
                    // An order can legitimately contain the same target URL twice,
                    // so the index is part of the key.
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`${url}-${index}`}>
                      <ExternalLink url={url} />
                    </div>
                  ))
                : EMPTY_VALUE}
            </div>
          </div>
          <div className="dropdown-backlinks-content__details-top__item">
            <div className="dropdown-backlinks-content__details-top__item__title">ANCHOR</div>
            <div className="dropdown-backlinks-content__details-top__text">
              {anchors.length ? anchors.join(', ') : EMPTY_VALUE}
            </div>
          </div>
          {isLinkInsertion ? (
            <div className="dropdown-backlinks-content__details-top__item">
              <div className="dropdown-backlinks-content__details-top__item__title">
                ARTICLE URL
              </div>
              <div className="dropdown-backlinks-content__details-top__text">
                <ExternalLink url={data.linkInsertionArticleUrl} />
              </div>
            </div>
          ) : null}
          {isLinkInsertion && data.linkInsertionComment ? (
            <div className="dropdown-backlinks-content__details-top__item">
              <div className="dropdown-backlinks-content__details-top__item__title">COMMENT</div>
              <div className="dropdown-backlinks-content__details-top__text">
                {data.linkInsertionComment}
              </div>
            </div>
          ) : null}
          <div className="dropdown-backlinks-content__details-top__item">
            <div className="dropdown-backlinks-content__details-top__item__title">
              PUBLICATION URL
            </div>
            <div className="dropdown-backlinks-content__details-top__text">
              <ExternalLink url={publicationUrl} />
            </div>
          </div>
        </div>
        <InsertionBrief url={briefUrl} {...INSERTION_BRIEF_COPY} />
      </div>
    </DropdownPanel>
  );
}

export default PlacementDetails;
